import { ComponentRegistration } from '@contentful/experiences-core/types';
import React from 'react';
interface CFProps extends React.HtmlHTMLAttributes<HTMLElement> {
  /**
   * Classes to be applied to the container component if `wrapComponent` is true, or directly to the child component if false.
   */
  className?: string;
  'data-cf-node-id': string;
  'data-cf-node-block-id': string;
  'data-cf-node-block-type': string;
}

// NOTE: We're use this as a default for `options`. When only one option is explicitly defined,
// all other default options are dropped as we're not merging the provided options with the default ones.
// We started merging both options with SDK v3 but many customers wouldn't upgrade as it suddenly added
// wrappers to their components because they only set the options to `{ wrapContainerWidth: "100%" }`.
// With v2, it results in `wrapComponent` being undefined but with v3, it resulted in it being `true` and
// thus causing unexpected layout changes.
// We rolled back that change to let customers migrate smoothly for now but will adjust this behaviour with
// v4.
const DEFAULT_OPTIONS = {
  wrapComponent: true,
  wrapContainer: 'div' as keyof JSX.IntrinsicElements,
};

/**
 * Sets up a component to be consumed by Experience Builder. This function can be used to wrap a component with a container component, or to pass props to the component directly.
 * @param Component Component to be used by Experience Builder.
 * @param options Options for the `withComponentWrapper` function.
 * @default { wrapComponent: true, wrapContainer: 'div' }
 * @returns A component that can be passed to `defineComponents`.
 */
export function withComponentWrapper<T>(
  Component: React.ElementType,
  options: ComponentRegistration['options'] = DEFAULT_OPTIONS,
) {
  const Wrapped: React.FC<CFProps & T> = (props) => {
    const Tag = options.wrapContainer ?? 'div';
    const {
      className = '',
      'data-cf-node-id': dataCfNodeId,
      'data-cf-node-block-id': dataCfNodeBlockId,
      'data-cf-node-block-type': dataCfNodeBlockType,
      ...componentProps
    } = props;
    const component = options.wrapComponent ? (
      <Tag
        data-component-wrapper
        className={className}
        data-cf-node-id={dataCfNodeId}
        data-cf-node-block-id={dataCfNodeBlockId}
        data-cf-node-block-type={dataCfNodeBlockType}>
        <Component {...componentProps} />
      </Tag>
    ) : (
      React.createElement(Component, props)
    );
    return component;
  };

  return Wrapped;
}
